import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { conversationFlowRuntimeSchema, type ConversationFlowRuntime } from '@eairlines/contracts';
import {
  analyzeKnowledge,
  flagKnowledgePassage,
  getAnalytics,
  getConversationFlow,
  simulateConversationFlow,
  submitBusinessRuleForApproval,
  submitConversationFlowForApproval,
  testIntentPhrase,
  updateBusinessRule,
  updateConversationFlow,
  validateConversationFlow,
} from '../api/client';
import { ConversationFlowBuilder } from '../components/conversation-flow-builder';

const FLOW_ID = '40000000-0000-4000-8000-000000000001';
const INTENT_ID = '50000000-0000-4000-8000-000000000001';
const PASSAGE_ID = '21000000-0000-4000-8000-000000000002';

type FeatureKind = 'knowledge' | 'rules' | 'flow' | 'intent' | 'analytics';

const meta: Record<FeatureKind, { title: string; description: string }> = {
  knowledge: {
    title: 'Knowledge Base',
    description: 'Review the approved sources and passages used to answer customer questions.',
  },
  rules: {
    title: 'Business Rules',
    description: 'Define deterministic decisions, eligibility conditions, exceptions and actions.',
  },
  flow: {
    title: 'Conversation Flow',
    description: 'Design, validate and simulate the customer journey before publication.',
  },
  intent: {
    title: 'Intent & Wording',
    description: 'Control how customer language is recognised and routed to this use case.',
  },
  analytics: {
    title: 'Use-case Analytics',
    description: 'Understand performance, friction, containment and handover outcomes.',
  },
};

export function FeaturePage({ kind }: { kind: FeatureKind }) {
  const readOnly = import.meta.env.VITE_READ_ONLY_PREVIEW === 'true';
  return (
    <main className="feature-page">
      <section className="page-heading feature-heading">
        <div>
          <h1>{meta[kind].title}</h1>
          <p>{meta[kind].description}</p>
        </div>
        <span className="version-pill">
          <i className="status-dot" /> Published v12
        </span>
      </section>
      {readOnly ? (
        <div className="preview-notice" role="status">
          This hosted preview is read-only. Explore, filter, validate and simulate without changing
          published configuration.
        </div>
      ) : null}
      {kind === 'knowledge' ? <KnowledgePage readOnly={readOnly} /> : null}
      {kind === 'rules' ? <RulesPage readOnly={readOnly} /> : null}
      {kind === 'flow' ? <FlowPage readOnly={readOnly} /> : null}
      {kind === 'intent' ? <IntentPage readOnly={readOnly} /> : null}
      {kind === 'analytics' ? <AnalyticsPage /> : null}
    </main>
  );
}

const documents = [
  {
    id: '20000000-0000-4000-8000-000000000001',
    name: 'Baggage Policy',
    version: 'v4.2',
    owner: 'Airport Operations',
    status: 'Approved',
    updated: '16 Sep 2026',
  },
  {
    id: '20000000-0000-4000-8000-000000000002',
    name: 'Conditions of Carriage',
    version: 'v8.1',
    owner: 'Legal',
    status: 'Approved',
    updated: '12 Sep 2026',
  },
  {
    id: '20000000-0000-4000-8000-000000000003',
    name: 'Guest Service Handbook',
    version: 'v5.7',
    owner: 'Guest Experience',
    status: 'Review due',
    updated: '03 Sep 2026',
  },
];

function KnowledgePage({ readOnly }: { readOnly: boolean }) {
  const [linkedDocuments, setLinkedDocuments] = useState(documents);
  const [selected, setSelected] = useState(documents[0]!.id);
  const [query, setQuery] = useState('');
  const analysis = useMutation({ mutationFn: analyzeKnowledge });
  const flag = useMutation({
    mutationFn: () =>
      flagKnowledgePassage(PASSAGE_ID, {
        assignee: 'Policy Operations',
        explanation: 'Confirm compensation wording against the approved Conditions of Carriage.',
      }),
  });
  const document = linkedDocuments.find((item) => item.id === selected)!;
  const visible = linkedDocuments.filter((item) => item.name.toLowerCase().includes(query.toLowerCase()));
  const linkSource = () => {
    const id = `local-source-${linkedDocuments.length + 1}`;
    setLinkedDocuments((items) => [...items, { id, name: `Local policy source ${items.length + 1}`, version: 'Draft', owner: 'Local preview', status: 'Review due', updated: 'Not published' }]);
    setSelected(id);
  };
  return (
    <div className="module-grid knowledge-layout">
      <section className="panel module-list">
        <div className="module-toolbar">
          <h2>Linked documents ({linkedDocuments.length})</h2>
          <button
            className="square-button"
            onClick={linkSource}
            title="Link an unsaved source in this browser preview."
            aria-label="Link local source"
          >
            ＋
          </button>
        </div>
        <input
          className="search-input"
          aria-label="Search documents"
          placeholder="Search linked sources"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        {visible.map((item) => (
          <button
            key={item.id}
            onClick={() => setSelected(item.id)}
            className={selected === item.id ? 'record-card selected' : 'record-card'}
          >
            <span className="record-icon">▤</span>
            <span>
              <strong>{item.name}</strong>
              <small>
                {item.version} · {item.owner}
              </small>
            </span>
            <span className={item.status === 'Approved' ? 'ready-pill' : 'warning-pill'}>
              {item.status}
            </span>
          </button>
        ))}
      </section>
      <section className="panel module-editor">
        <div className="module-toolbar">
          <div>
            <h2>{document.name}</h2>
            <small>{document.version} · Effective 01 Oct 2026</small>
          </div>
          <span className="locked-pill">Read-only source</span>
        </div>
        <div className="source-meta">
          <span>
            Owner <strong>{document.owner}</strong>
          </span>
          <span>
            Market <strong>UAE</strong>
          </span>
          <span>
            Language <strong>English</strong>
          </span>
          <span>
            Updated <strong>{document.updated}</strong>
          </span>
        </div>
        <h3>Applicable passages</h3>
        <article className="passage-card">
          <div>
            <strong>Delayed baggage reporting</strong>
            <span className="ready-pill">92% relevant</span>
          </div>
          <p>
            Guests must report delayed baggage before leaving the arrivals hall. A Property
            Irregularity Report reference is required for follow-up.
          </p>
          <small>Section 7.3 · Applies to baggage-status enquiries</small>
        </article>
        <article className="passage-card warning-border">
          <div>
            <strong>Compensation guidance</strong>
            <span className="warning-pill">Possible contradiction</span>
          </div>
          <p>
            Reimbursement is assessed after review of receipts and route-specific policy. The
            assistant must not promise a fixed amount.
          </p>
          <small>Section 9.1 · Related to Conditions of Carriage §14</small>
        </article>
        <button
          className="button secondary"
          onClick={() => flag.mutate()}
          disabled={readOnly || flag.isPending || flag.isSuccess}
        >
          {flag.isSuccess
            ? 'Review flag created'
            : flag.isPending
              ? 'Creating flag…'
              : 'Flag passage for review'}
        </button>
        {flag.isError ? (
          <p className="form-error" role="alert">
            {flag.error.message}
          </p>
        ) : null}
      </section>
      <aside className="panel insight-panel">
        <div className="module-toolbar">
          <h2>Knowledge health</h2>
          <strong className="score">86</strong>
        </div>
        <p className="muted">Evidence-based checks for the currently linked sources.</p>
        <div className="finding">
          <strong>2 possible contradictions</strong>
          <p>Compensation wording differs between two approved sources.</p>
        </div>
        <div className="finding">
          <strong>1 review overdue</strong>
          <p>The Guest Service Handbook review date has passed.</p>
        </div>
        {analysis.data ? (
          <div className="success-banner compact">✓ {analysis.data.summary}</div>
        ) : null}
        {analysis.isError ? (
          <p className="form-error" role="alert">
            {analysis.error.message}
          </p>
        ) : null}
        <button
          className="button primary full-width"
          onClick={() => analysis.mutate()}
          disabled={analysis.isPending}
        >
          {analysis.isPending ? 'Analysing…' : 'Run knowledge analysis'}
        </button>
      </aside>
    </div>
  );
}

const rules = [
  {
    id: '30000000-0000-4000-8000-000000000001',
    name: 'Eligible disruption rebooking',
    priority: 'HIGH',
    status: 'Published',
  },
  {
    id: '30000000-0000-4000-8000-000000000002',
    name: 'Meal voucher eligibility',
    priority: 'MEDIUM',
    status: 'Published',
  },
];

function RulesPage({ readOnly }: { readOnly: boolean }) {
  const [localRules, setLocalRules] = useState(rules);
  const [selected, setSelected] = useState(rules[0]!.id);
  const [description, setDescription] = useState(
    'Allow an affected guest to move to the next available flight when the disruption meets policy thresholds.',
  );
  const [changeReason, setChangeReason] = useState(
    'Align disruption handling with September policy release',
  );
  const [showVersions, setShowVersions] = useState(false);
  const [localSaved, setLocalSaved] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      updateBusinessRule(selected, {
        description,
        priority: 'HIGH',
        conditions: ['Flight status is cancelled', 'Ticket stock is airline controlled'],
        action: 'Offer up to three eligible rebooking options in the same cabin.',
        exceptions: ['Unaccompanied minor', 'Group booking', 'Partner-issued ticket'],
        requiredData: ['bookingReference', 'surname', 'flightStatus'],
        dependencies: ['Flight Status API', 'Rebooking Inventory'],
        implementationInstruction: 'Evaluate all conditions before calling rebooking inventory.',
        changeReason,
        expectedVersion: 12,
      }),
  });
  const approval = useMutation({
    mutationFn: () =>
      submitBusinessRuleForApproval(selected, {
        expectedVersion: save.data?.version ?? 12,
        reason: 'Request approval after policy and deterministic-rule review.',
      }),
  });
  const active = localRules.find((rule) => rule.id === selected)!;
  const isLocal = active.id.startsWith('local-rule-');
  const createRule = () => {
    const id = `local-rule-${localRules.length + 1}`;
    setLocalRules((items) => [...items, { id, name: `Untitled local rule ${items.length + 1}`, priority: 'MEDIUM', status: 'Local draft' }]);
    setSelected(id);
    setDescription('Describe the deterministic outcome for this local draft.');
    setLocalSaved(false);
  };
  return (
    <div className="module-grid rules-layout">
      <section className="panel module-list">
        <div className="module-toolbar">
          <h2>Rules ({localRules.length})</h2>
          <button
            className="square-button"
            onClick={createRule}
            title="Create an unsaved local rule draft."
            aria-label="Create local rule"
          >
            ＋
          </button>
        </div>
        {localRules.map((rule) => (
          <button
            key={rule.id}
            className={selected === rule.id ? 'record-card selected' : 'record-card'}
            onClick={() => {
              setSelected(rule.id);
              save.reset();
            }}
          >
            <span className="record-icon">◆</span>
            <span>
              <strong>{rule.name}</strong>
              <small>
                {rule.status} · Priority {rule.priority}
              </small>
            </span>
            <span>›</span>
          </button>
        ))}
      </section>
      <section className="panel rule-builder">
        <div className="module-toolbar">
          <div>
            <h2>{active.name}</h2>
            <small>Version 12 · Owner: Journey Operations</small>
          </div>
          <span className="ready-pill">{active.status}</span>
        </div>
        <label>
          Plain-language purpose
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            disabled={readOnly && !isLocal}
          />
        </label>
        <h3>When all conditions match</h3>
        <div className="logic-row">
          <span>Flight status</span>
          <select disabled={readOnly} defaultValue="cancelled">
            <option value="cancelled">is cancelled</option>
          </select>
        </div>
        <div className="logic-row">
          <span>Ticket control</span>
          <select disabled={readOnly} defaultValue="airline">
            <option value="airline">is airline controlled</option>
          </select>
        </div>
        <h3>Then</h3>
        <div className="action-card">
          <strong>Offer rebooking</strong>
          <p>Search the same cabin within ±2 days and present at most three eligible options.</p>
        </div>
        <h3>Exceptions</h3>
        <div className="tag-row">
          <span className="chip">Unaccompanied minor</span>
          <span className="chip">Group booking</span>
          <span className="chip">Partner-issued ticket</span>
        </div>
        <label>
          Reason for change
          <input
            value={changeReason}
            onChange={(event) => setChangeReason(event.target.value)}
            disabled={readOnly && !isLocal}
          />
        </label>
        <button
          className="button primary full-width"
          disabled={
            (!isLocal && readOnly) ||
            save.isPending ||
            description.trim().length < 1 ||
            changeReason.trim().length < 10
          }
          onClick={() => isLocal ? setLocalSaved(true) : save.mutate()}
        >
          {localSaved
            ? 'Local draft saved'
            : save.isSuccess
            ? `Draft v${save.data.version} saved`
            : save.isPending
              ? 'Saving…'
              : 'Save as new draft'}
        </button>
        {save.isError ? (
          <p className="form-error" role="alert">
            {save.error.message}
          </p>
        ) : null}
        <button
          className="button secondary full-width"
          disabled={isLocal || readOnly || !save.data || approval.isPending}
          onClick={() => approval.mutate()}
        >
          {approval.isSuccess
            ? 'Awaiting approval'
            : approval.isPending
              ? 'Submitting…'
              : 'Submit draft for approval'}
        </button>
        {approval.isError ? (
          <p className="form-error" role="alert">
            {approval.error.message}
          </p>
        ) : null}
      </section>
      <aside className="panel insight-panel">
        <div className="module-toolbar">
          <h2>Platform translation</h2>
          <span className="ready-pill">✓ Ready</span>
        </div>
        <pre className="code-block">
          IF flight.status = CANCELLED{`\n`}AND ticket.stock = EY{`\n`}THEN search_rebooking{`\n`}
          LIMIT options = 3
        </pre>
        <div className="finding">
          <strong>Dependency</strong>
          <p>Flight status API · Rebooking inventory service</p>
        </div>
        <div className="finding warning-border">
          <strong>1 non-blocking overlap</strong>
          <p>Meal voucher eligibility uses the same disruption threshold.</p>
        </div>
        <button
          className="button secondary full-width"
          onClick={() => setShowVersions((value) => !value)}
        >
          View version history
        </button>
        {showVersions ? (
          <div aria-label="Version history">
            {save.data ? <div className="finding"><strong>v{save.data.version} · Draft</strong><p>{changeReason} · Current user</p></div> : null}
            <div className="finding"><strong>v12 · Published</strong><p>September policy release · Journey Operations</p></div>
            <div className="finding"><strong>v11 · Superseded</strong><p>Clarified partner-ticket exception · Journey Operations</p></div>
            <div className="finding"><strong>v10 · Superseded</strong><p>Added deterministic option limit · Platform Governance</p></div>
          </div>
        ) : null}
      </aside>
    </div>
  );
}

const defaultWorkflow: ConversationFlowRuntime = {
  schema_version: 1,
  entry_step: 'greet_guest',
  routing: {
    acknowledgment_required: true,
    acknowledgment_description: 'A short phrase to say while switching steps.',
    transition_description_template: 'Call as soon as {when}.',
    continuation_directive: 'Continue directly with the next step without re-greeting.',
    continuation_directive_no_filler: 'Continue directly with the next step without re-greeting.',
  },
  shared_prompt: 'You are {agent_name}, an airline support agent. Ask one question at a time and never invent operational information.',
  state_fields: [
    { name: 'booking_reference', type: 'string', description: 'The guest booking reference.' },
    { name: 'surname', type: 'string', description: 'The surname on the booking.' },
  ],
  steps: [
    { id: 'greet_guest', type: 'llm', prompt: 'Ask for the booking reference and surname.', tools: [], collect: ['booking_reference', 'surname'], transitions: [{ to: 'verify_booking', when: 'both booking reference and surname are available' }] },
    { id: 'verify_booking', type: 'llm', prompt: 'Confirm the booking details and explain the next verification step.', tools: [], collect: [], transitions: [{ to: 'complete', when: 'the guest confirms the details' }] },
    { id: 'complete', type: 'end', final_message: 'Thank you. Continue through the approved airline channel.', terminate_call: true },
  ],
  global_steps: [{ id: 'say_goodbye', type: 'end', when: 'the guest clearly says goodbye', final_message: 'Thank you. Goodbye.', terminate_call: true, exclude: [] }],
  tools: [],
};
const defaultLayout = { greet_guest: { x: 40, y: 120 }, verify_booking: { x: 360, y: 120 }, complete: { x: 680, y: 120 }, say_goodbye: { x: 360, y: 400 } };

function FlowPage({ readOnly }: { readOnly: boolean }) {
  const flowQuery = useQuery({ queryKey: ['conversation-flow', FLOW_ID], queryFn: () => getConversationFlow(FLOW_ID) });
  const [workflow, setWorkflow] = useState<ConversationFlowRuntime>(defaultWorkflow);
  const [layout, setLayout] = useState<Record<string, { x: number; y: number }>>(defaultLayout);
  const [dirty, setDirty] = useState(false);
  const localValidation = useMemo(() => conversationFlowRuntimeSchema.safeParse(workflow), [workflow]);
  useEffect(() => {
    if (!flowQuery.data || dirty) return;
    setWorkflow(flowQuery.data.workflow);
    setLayout(flowQuery.data.layout);
  }, [dirty, flowQuery.data]);
  const validation = useMutation({ mutationFn: () => validateConversationFlow(FLOW_ID) });
  const simulation = useMutation({
    mutationFn: () =>
      simulateConversationFlow(FLOW_ID, {
        message: 'My flight to London was cancelled. Can I move to tomorrow?',
        persona: 'disrupted guest',
        expectedVersion: flowQuery.data?.version ?? 8,
      }),
  });
  const save = useMutation({
    mutationFn: () => updateConversationFlow(FLOW_ID, { workflow, layout, changeReason: 'Update the governed visual conversation flow.', expectedVersion: flowQuery.data?.version ?? 8 }),
  });
  const approval = useMutation({
    mutationFn: () =>
      submitConversationFlowForApproval(FLOW_ID, {
        expectedVersion: save.data?.version ?? flowQuery.data?.version ?? 8,
        reason: 'Request approval after successful flow validation.',
      }),
  });
  return (
    <div className="flow-layout runtime-flow-layout">
      <section className="flow-toolbar">
        <button
          className="button secondary"
          onClick={() => validation.mutate()}
          disabled={validation.isPending}
        >
          {validation.isPending ? 'Validating…' : dirty ? 'Validate saved flow' : 'Validate runtime JSON'}
        </button>
        <button className="button secondary" disabled={readOnly || save.isPending || !dirty || !localValidation.success} onClick={() => save.mutate()}>{save.isSuccess ? `Draft v${save.data.version} saved` : save.isPending ? 'Saving…' : 'Save governed draft'}</button>
        <button
          className="button secondary"
          disabled={readOnly || !save.data || !validation.data?.valid || approval.isPending}
          onClick={() => approval.mutate()}
        >
          {approval.isSuccess
            ? 'Awaiting approval'
            : approval.isPending
              ? 'Submitting…'
              : 'Submit for approval'}
        </button>
        <button
          className="button primary"
          onClick={() => simulation.mutate()}
          disabled={simulation.isPending}
        >
          {simulation.isPending ? 'Simulating…' : '▶ Run simulation'}
        </button>
      </section>
      <div className={localValidation.success ? 'success-banner compact' : 'alert warning'} role="status">
        {localValidation.success ? '✓ Runtime JSON matches the NextLevel conversation_flow v1 contract.' : `Runtime JSON has ${localValidation.error.issues.length} contract issue(s).`}
      </div>
      {save.isError ? <p className="form-error" role="alert">{save.error.message}</p> : null}
      {flowQuery.isError ? <p className="form-error" role="alert">Could not load the persisted flow: {flowQuery.error.message}</p> : null}
      <ConversationFlowBuilder workflow={workflow} layout={layout} readOnly={readOnly} onChange={(nextWorkflow, nextLayout) => { setWorkflow(nextWorkflow); setLayout(nextLayout); setDirty(true); save.reset(); validation.reset(); }} />
      <aside className="panel simulation-panel runtime-simulation-panel">
          <div className="module-toolbar">
            <h2>Validation & simulation</h2>
            <span className={validation.data?.valid ? 'ready-pill' : 'warning-pill'}>
              {validation.data?.valid ? 'Valid' : 'Not run'}
            </span>
          </div>
          {validation.data ? (
            <>
              <div className="success-banner compact">
                ✓ No dead ends or unreachable steps detected.
              </div>
              <div className="finding">
                <strong>Advisory</strong>
                <p>{validation.data.findings[0]?.message ?? 'No advisory findings.'}</p>
              </div>
            </>
          ) : (
            <p className="muted inset">Validate this exact draft before requesting approval.</p>
          )}
          {validation.isError ? (
            <p className="form-error" role="alert">
              {validation.error.message}
            </p>
          ) : null}
          <div className="sim-chat">
            <div className="customer-message">
              My flight to London was cancelled. Can I move to tomorrow?
            </div>
            {simulation.data ? (
              <>
                <div className="assistant-message">
                  {
                    simulation.data.transcript.find((entry) => entry.speaker === 'ASSISTANT')
                      ?.message
                  }
                </div>
                <div className="decision-trace">
                  <strong>Decision trace</strong>
                  <small>{simulation.data.decisionTrace[0]?.decision}</small>
                  <small>
                    Guardrails: {simulation.data.decisionTrace[0]?.guardrails.join(', ')}
                  </small>
                  <small>{simulation.data.decisionTrace[0]?.durationMs} ms</small>
                </div>
              </>
            ) : (
              <p className="muted">
                Run a deterministic simulation to inspect the transcript and decision trace.
              </p>
            )}
            {simulation.isError ? (
              <p className="form-error" role="alert">
                {simulation.error.message}
              </p>
            ) : null}
          </div>
        </aside>
    </div>
  );
}

const phraseGroups = {
  'Core phrases': ['change my flight', 'move my booking', 'rebook my trip'],
  Keywords: ['rebook', 'change', 'another flight'],
  Synonyms: ['reschedule', 'move', 'switch'],
  'Local terminology': ['move my sector', 'change my journey'],
};

function IntentPage({ readOnly }: { readOnly: boolean }) {
  const [groups, setGroups] = useState(phraseGroups);
  const [newPhrase, setNewPhrase] = useState('');
  const [description, setDescription] = useState('The guest wants to change the date, time or flight associated with an existing booking.');
  const [message, setMessage] = useState('Can you move me to tomorrow’s flight?');
  const test = useMutation({
    mutationFn: () => testIntentPhrase(INTENT_ID, { message, expectedVersion: 9 }),
  });
  return (
    <div className="module-grid intent-layout">
      <section className="panel phrase-panel">
        <div className="module-toolbar">
          <div>
            <h2>Change or rebook flight</h2>
            <small>Intent v9 · Published</small>
          </div>
          <span className="ready-pill">Active</span>
        </div>
        <label>
          Description
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            title="Unsaved local intent preview"
          />
        </label>
        {Object.entries(groups).map(([group, phrases]) => (
          <div className="phrase-group" key={group}>
            <div>
              <strong>{group}</strong>
              <button onClick={() => {
                const phrase = newPhrase.trim() || `local ${group.toLowerCase()} phrase`;
                setGroups((value) => ({ ...value, [group]: [...value[group as keyof typeof value], phrase] }));
                setNewPhrase('');
              }} title="Add an unsaved local phrase">＋ Add</button>
            </div>
            <div className="tag-row">
              {phrases.map((phrase) => (
                <span className="chip" key={phrase}>
                  {phrase} <button aria-label={`Remove ${phrase}`} onClick={() => setGroups((value) => ({ ...value, [group]: value[group as keyof typeof value].filter((item) => item !== phrase) }))}>×</button>
                </span>
              ))}
            </div>
          </div>
        ))}
        <label>New local phrase<input value={newPhrase} onChange={(event) => setNewPhrase(event.target.value)} placeholder="Type a training phrase" /></label>
        <small className="muted">Local preview only. {readOnly ? 'Hosted publishing remains disabled.' : 'Save through the governed workflow when ready.'}</small>
      </section>
      <section className="panel competing-panel">
        <div className="module-toolbar">
          <h2>Competing intents</h2>
          <span className="warning-pill">12% overlap</span>
        </div>
        <div className="intent-card">
          <strong>Flight disruption help</strong>
          <p>Strong overlap when a customer mentions cancellation before asking to move.</p>
          <div className="meter">
            <i style={{ width: '64%' }} />
          </div>
        </div>
        <div className="intent-card">
          <strong>Cancel booking</strong>
          <p>Differentiate “change” from explicit cancellation language.</p>
          <div className="meter">
            <i style={{ width: '31%' }} />
          </div>
        </div>
        <div className="alert warning">
          <strong>△ Clarification recommended</strong>
          <p>Ask whether the guest wants to cancel or move when confidence is below 75%.</p>
        </div>
      </section>
      <aside className="panel tester-panel">
        <div className="module-toolbar">
          <h2>Phrase tester</h2>
        </div>
        <label>
          Customer message
          <textarea value={message} onChange={(event) => setMessage(event.target.value)} />
        </label>
        <button
          className="button primary full-width"
          onClick={() => test.mutate()}
          disabled={test.isPending}
        >
          {test.isPending ? 'Testing…' : 'Test phrase'}
        </button>
        {test.data ? (
          <div className="test-result">
            <span className="ready-pill">{Math.round(test.data.confidence * 100)}% confidence</span>
            <h3>{test.data.detectedIntent}</h3>
            <p>
              <strong>Trigger:</strong> {test.data.triggeringLanguage.join(' · ')}
            </p>
            <p>
              <strong>Entity:</strong>{' '}
              {test.data.entities.map((entity) => `${entity.type} = ${entity.value}`).join(', ') ||
                'None'}
            </p>
            <p>
              <strong>Route:</strong> {test.data.selectedUseCase} use case
            </p>
            <small>
              {test.data.clarification.required
                ? test.data.clarification.message
                : 'No clarification required.'}
            </small>
          </div>
        ) : null}
        {test.isError ? (
          <p className="form-error" role="alert">
            {test.error.message}
          </p>
        ) : null}
      </aside>
    </div>
  );
}

function AnalyticsPage() {
  const [range, setRange] = useState('30');
  const multiplier = Number(range) / 30;
  const to = '2026-09-18';
  const from = range === '7' ? '2026-09-12' : range === '90' ? '2026-06-21' : '2026-08-20';
  const dashboard = useQuery({
    queryKey: ['analytics', from, to],
    queryFn: () => getAnalytics(from, to),
  });
  const dashboardMetrics = dashboard.data?.metrics;
  const metrics = useMemo<Array<[string, string, string]>>(
    () => [
      [
        'Conversations',
        Math.round((dashboardMetrics?.conversations ?? 12840) * multiplier).toLocaleString(),
        '+8.4%',
      ],
      ['Containment', `${dashboardMetrics?.containmentRate ?? 72.6}%`, '+3.1%'],
      ['Successful outcomes', `${dashboardMetrics?.successfulOutcomeRate ?? 81.2}%`, '+1.8%'],
      ['Handovers', `${dashboardMetrics?.handoverRate ?? 18.4}%`, '-2.3%'],
      ['CSAT', `${dashboardMetrics?.csat ?? 4.42} / 5`, '+0.12'],
    ],
    [dashboardMetrics, multiplier],
  );
  const exportReport = () => {
    const rows = [['metric', 'value'], ...metrics.map(([label, value]) => [label, value])];
    const csv = rows.map((row) => row.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url; anchor.download = `eairlines-analytics-${from}-${to}.csv`; anchor.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="analytics-page">
      <section className="analytics-toolbar">
        <div>
          <label htmlFor="range">Date range</label>
          <select id="range" value={range} onChange={(event) => setRange(event.target.value)}>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </div>
        <button className="button secondary" onClick={exportReport}>
          Export report
        </button>
      </section>
      {dashboard.isError ? (
        <p className="form-error" role="alert">
          Analytics could not be loaded: {dashboard.error.message}
        </p>
      ) : null}
      <section className="metric-grid">
        {metrics.map(([label, value, delta]) => (
          <article className="metric-card" key={label}>
            <small>{label}</small>
            <strong>{value}</strong>
            <span
              className={delta.startsWith('-') && label !== 'Handovers' ? 'negative' : 'positive'}
            >
              {delta} vs previous period
            </span>
          </article>
        ))}
      </section>
      <section className="chart-grid">
        <article className="panel chart-card">
          <div className="module-toolbar">
            <h2>Conversation trend</h2>
            <span className="muted">Daily volume</span>
          </div>
          <div className="bar-chart">
            {[44, 58, 51, 72, 63, 81, 69, 88, 77, 92, 84, 96].map((height, index) => (
              <i
                key={index}
                style={{ height: `${height}%` }}
                title={`${height * 12} conversations`}
              />
            ))}
          </div>
        </article>
        <article className="panel chart-card">
          <div className="module-toolbar">
            <h2>Sentiment</h2>
          </div>
          <div className="donut">
            <strong>68%</strong>
            <span>positive</span>
          </div>
          <div className="legend-row">
            <span>● Positive 68%</span>
            <span>● Neutral 23%</span>
            <span>● Negative 9%</span>
          </div>
        </article>
        <article className="panel chart-card">
          <div className="module-toolbar">
            <h2>Outcome funnel</h2>
          </div>
          {[
            ['Intent recognised', 100],
            ['Booking identified', 84],
            ['Options presented', 76],
            ['Change completed', 61],
          ].map(([label, value]) => (
            <div className="funnel-row" key={label}>
              <span>{label}</span>
              <i style={{ width: `${value}%` }} />
              <strong>{value}%</strong>
            </div>
          ))}
        </article>
        <article className="panel recommendation-card">
          <div className="module-toolbar">
            <h2>Evidence → recommendation</h2>
            <span className="ready-pill">High confidence</span>
          </div>
          <h3>Reduce verification abandonment</h3>
          <p>
            <strong>Evidence:</strong> 14% of eligible sessions end while customers search for their
            booking reference.
          </p>
          <p>
            <strong>Inference:</strong> The prompt does not explain where the reference appears.
          </p>
          <p>
            <strong>Recommended action:</strong> Add a concise location hint and an email-search
            example.
          </p>
          <p className="muted">
            Estimated impact: +3–5 points completion. Configuration changes still require approval.
          </p>
        </article>
      </section>
    </div>
  );
}
